import {
  CapacityPlanUploadAbortedError,
  uploadCapacityPlanFile,
} from 'core/apis/capacityPlanUpload';
import { RunSettingsInput } from 'core/graphql/types';
import useCountryContext from 'core/hooks/useCountryContext';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';

export interface CapacityPlanUploadProgress {
  bytesUploaded: number;
  bytesTotal: number;
  /** 0..100, rounded. */
  percentage: number;
}

export interface CapacityPlanUploadRequest {
  file: File;
  settings: RunSettingsInput;
  /** Guid of the Agency, or null for a Network Run (no `agencyId` field). */
  agencyId: string | null;
  label: string | null;
}

export interface CapacityPlanUpload {
  isUploading: boolean;
  progress: CapacityPlanUploadProgress | null;
  /** Uploads the file; resolves with the new Run's id, or null when it failed or was cancelled. */
  start: (request: CapacityPlanUploadRequest) => Promise<string | null>;
  /** Aborts the request; the API rolls back the Run on its own. */
  cancel: () => void;
}

const progressOf = (
  bytesUploaded: number,
  bytesTotal: number,
): CapacityPlanUploadProgress => ({
  bytesUploaded,
  bytesTotal,
  percentage:
    bytesTotal === 0 ? 0 : Math.round((bytesUploaded / bytesTotal) * 100),
});

/**
 * Drives the single-request gzip upload of a deliveries file: the API
 * decompresses, parses and stores the Run and its Deliveries in one
 * transaction, so the Run only exists once the response arrives (ADR-0004).
 */
const useCapacityPlanUpload = (): CapacityPlanUpload => {
  const auth = useAuth();
  const { countryContext } = useCountryContext();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<CapacityPlanUploadProgress | null>(
    null,
  );
  const abortRef = useRef<AbortController | null>(null);

  const accessToken = auth.user?.access_token;

  const headers = useMemo(() => {
    const value: Record<string, string> = {};
    if (accessToken) {
      value.Authorization = `Bearer ${accessToken}`;
    }
    if (countryContext) {
      value['X-Tenant-Country'] = countryContext;
    }

    return value;
  }, [accessToken, countryContext]);

  const start = useCallback(
    async ({ file, settings, agencyId, label }: CapacityPlanUploadRequest) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setIsUploading(true);
      setProgress(progressOf(0, file.size));

      try {
        const { runId } = await uploadCapacityPlanFile({
          endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/capacity-plans/runs`,
          file,
          headers,
          settings: JSON.stringify(settings),
          ...(agencyId ? { agencyId } : {}),
          ...(label ? { label } : {}),
          signal: controller.signal,
          onProgress: (bytesUploaded, bytesTotal) =>
            setProgress(progressOf(bytesUploaded, bytesTotal)),
        });

        return runId;
      } catch (error) {
        // A cancel is the supervisor's own doing, so it is not an error.
        if (!(error instanceof CapacityPlanUploadAbortedError)) {
          toast.error(
            error instanceof Error ? error.message : 'The upload failed.',
          );
        }

        return null;
      } finally {
        abortRef.current = null;
        setIsUploading(false);
        setProgress(null);
      }
    },
    [headers],
  );

  const cancel = useCallback(() => {
    // The API rolls the transaction back on its own; nothing to follow up on.
    abortRef.current?.abort();
  }, []);

  return { isUploading, progress, start, cancel };
};

export default useCapacityPlanUpload;
